import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpImg from "./helpImg.png";
import "./index.scss";
import { TranslateProvider, useTranslation } from "../i18n";

function VRViewer({
  isShow,
  onClose,
  folder,
  xCount,
  yCount,
  langCode = "en"
}) {
  const {
    _t,
    setLanguage
  } = useTranslation();
  useEffect(() => {
    setLanguage(langCode);
  }, [langCode, setLanguage]);
  const [beforeMoves, setBeforeMoves] = useState(true);
  const [mousedown, setMousedown] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const minSwipeX = 10;
  const minSwipeY = 10;
  const [nextActionX, setNextActionX] = useState(0);
  const [backActionX, setBackActionX] = useState(0);
  const [nextActionY, setNextActionY] = useState(0);
  const [backActionY, setBackActionY] = useState(0);
  const [slideX, setSlideX] = useState(0);
  const [slideY, setSlideY] = useState(0);
  const [slides, setSlides] = useState({});
  const zoomOptions = {
    min: 1,
    max: 1.7,
    switch: 0.2
  };
  const [currentZoom, setCurrentZoom] = useState(1);

  function close() {
    // Reset all static values
    setBeforeMoves(true);
    setDragStartX(0);
    setDragStartY(0);
    setNextActionX(0);
    setBackActionX(0);
    setNextActionY(0);
    setBackActionY(0);
    setSlideX(0);
    setSlideY(0);
    setSlides({}); // Close viewerVR

    onClose();
  }

  useEffect(() => {
    if (isShow && folder) {
      let slidesForLoad = {};

      for (let y = 0; y <= yCount; y++) {
        for (let x = 0; x <= xCount; x++) {
          slidesForLoad[y + "_" + x] = require(`../mediaContent/VRPacks/${folder}/${y}_${x}.jpg`);
        }
      }

      setSlides({});
      Object.keys(slidesForLoad).forEach((id, index) => {
        imgLoader(slidesForLoad[id]).then(src => {
          setSlides(prevSlides => ({ ...prevSlides,
            [id]: src
          }));
        });
      });
    }
  }, [isShow, folder, xCount, yCount]);

  function imgLoader(src) {
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.src = src;

      img.onload = function () {
        resolve(src);
      };

      img.onerror = function () {
        reject();
      };
    });
  }

  const moveLeft = useCallback(() => {
    if (slideX > 0) {
      setSlideX(slideX - 1);
    } else if (slideX === 0) {
      setSlideX(xCount);
    }
  }, [slideX, xCount]);
  const moveRight = useCallback(() => {
    if (slideX < xCount) {
      setSlideX(slideX + 1);
    } else if (slideX === xCount) {
      setSlideX(0);
    }
  }, [slideX, xCount]);
  const moveDown = useCallback(() => {
    if (slideY < yCount) {
      setSlideY(slideY + 1);
    }
  }, [slideY, yCount]);
  const moveUp = useCallback(() => {
    if (slideY > 0) {
      setSlideY(slideY - 1);
    }
  }, [slideY]);
  const handleMousedown = useCallback(e => {
    if (!e.touches) {
      e.preventDefault();
    }

    setBeforeMoves(false);
    setMousedown(true);
    setDragStartX(e?.touches ? e.touches[0].clientX : e.clientX);
    setDragStartY(e?.touches ? e.touches[0].clientY : e.clientY);
    setNextActionX(dragStartX - minSwipeX);
    setBackActionX(dragStartX + minSwipeX);
    setNextActionY(dragStartY - minSwipeY);
    setBackActionY(dragStartY + minSwipeY);
  }, [dragStartX, dragStartY]);
  const handleMouseup = useCallback(() => {
    setMousedown(false);
  }, []);
  const handleMousemove = useCallback(e => {
    if (!mousedown) {
      return;
    }

    const eventPosX = e?.touches ? e.touches[0].clientX : e.clientX;
    const eventPosY = e?.touches ? e.touches[0].clientY : e.clientY;

    if (eventPosX <= nextActionX) {
      setNextActionX(nextActionX - minSwipeX);
      setBackActionX(backActionX - minSwipeX);

      if (eventPosY <= nextActionY + minSwipeY * 2 / 4) {
        setNextActionY(nextActionY - minSwipeY);
        setBackActionY(backActionY - minSwipeY);
        moveUp();
        moveLeft();
      } else if (eventPosY >= backActionY - minSwipeY * 2 / 4) {
        setNextActionY(nextActionY + minSwipeY);
        setBackActionY(backActionY + minSwipeY);
        moveDown();
        moveLeft();
      } else {
        moveLeft();
      }
    } else if (eventPosX >= backActionX) {
      setNextActionX(nextActionX + minSwipeX);
      setBackActionX(backActionX + minSwipeX);

      if (eventPosY <= nextActionY + minSwipeY * 2 / 4) {
        setNextActionY(nextActionY - minSwipeY);
        setBackActionY(backActionY - minSwipeY);
        moveUp();
        moveRight();
      } else if (eventPosY >= backActionY - minSwipeY * 2 / 4) {
        setNextActionY(nextActionY + minSwipeY);
        setBackActionY(backActionY + minSwipeY);
        moveDown();
        moveRight();
      } else {
        moveRight();
      }
    } else if (eventPosY <= nextActionY) {
      setNextActionY(nextActionY - minSwipeY);
      setBackActionY(backActionY - minSwipeY);
      moveUp();
    } else if (eventPosY >= backActionY) {
      setNextActionY(nextActionY + minSwipeY);
      setBackActionY(backActionY + minSwipeY);
      moveDown();
    } // setNextSlide();

  }, [backActionX, backActionY, mousedown, moveDown, moveLeft, moveRight, moveUp, nextActionX, nextActionY]);
  const handleWheel = useCallback(e => {
    e = e || window.event;
    e.preventDefault ? e.preventDefault() : e.returnValue = false;

    if (Math.sign(e.deltaY) === 1) {
      if (currentZoom > zoomOptions.min) {
        setCurrentZoom(currentZoom - zoomOptions.switch);
      }
    } else {
      if (currentZoom <= zoomOptions.max) {
        setCurrentZoom(currentZoom + zoomOptions.switch);
      }
    }
  }, [currentZoom, zoomOptions.max, zoomOptions.min, zoomOptions.switch]); // Set slides according to slides amount and cords x and y

  const [currentSlide, setCurrentSlide] = useState(null);
  useEffect(() => {
    const slideKey = `${slideY}_${slideX}`;
    let slideSrc = slides.hasOwnProperty(slideKey) ? slides[slideKey] : null;
    setCurrentSlide(slideSrc);
  }, [slides, slideY, slideX]);
  const viewerRef = useRef();
  useEventListener(viewerRef, "touchstart", handleMousedown);
  useEventListener(viewerRef, "touchend", handleMouseup);
  useEventListener(viewerRef, "touchmove", handleMousemove);
  useEventListener(viewerRef, "mousedown", handleMousedown);
  useEventListener(viewerRef, "mouseup", handleMouseup);
  useEventListener(viewerRef, "mousemove", handleMousemove);
  useEventListener(viewerRef, "wheel", handleWheel);

  if (!isShow) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "viewerVR"
  }, /*#__PURE__*/React.createElement("div", {
    className: "videoFullScreen-backdrop",
    onClick: close
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    color: "primary",
    size: "small",
    className: "viewerVR-close",
    onClick: close,
    endIcon: /*#__PURE__*/React.createElement(CloseIcon, null)
  }, _t("Закрыть")), /*#__PURE__*/React.createElement("div", {
    className: "viewerVR-top"
  }), /*#__PURE__*/React.createElement("div", {
    className: "viewerVR-container",
    ref: viewerRef
  }, /*#__PURE__*/React.createElement("img", {
    className: "viewerVR-container-img",
    src: currentSlide,
    style: {
      transform: "scale(" + currentZoom + ")"
    },
    alt: ""
  }), /*#__PURE__*/React.createElement(Fade, {
    in: beforeMoves
  }, /*#__PURE__*/React.createElement("div", {
    className: "viewerVR-container-helper"
  }, /*#__PURE__*/React.createElement("img", {
    src: HelpImg,
    alt: ""
  }), /*#__PURE__*/React.createElement("span", null, _t("Начните вращать модель пальцем или мышью"))))), /*#__PURE__*/React.createElement("div", {
    className: "viewerVR-bottom"
  }));
}

function useEventListener(target, type, listener, ...options) {
  useEffect(() => {
    const targetIsRef = target.hasOwnProperty("current");
    const currentTarget = targetIsRef ? target.current : target;

    if (currentTarget) {
      currentTarget.addEventListener(type, listener, ...options);
    }

    return () => {
      if (currentTarget) {
        currentTarget.removeEventListener(type, listener, ...options);
      }
    };
  }, [target, type, listener, options]);
}

VRViewer.propTypes = {
  isShow: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folder: PropTypes.string.isRequired,
  xCount: PropTypes.number.isRequired,
  yCount: PropTypes.number.isRequired
};

const VRViewerWrapper = props => {
  return /*#__PURE__*/React.createElement(TranslateProvider, null, /*#__PURE__*/React.createElement(VRViewer, props));
};

export default VRViewerWrapper;